// Legal handler report (Emma): who holds what, what is overdue, what got done — by week.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Download, Loader2, RefreshCw, Users } from "lucide-react";
import { toast } from "sonner";
import { getLegalHandlerReport, downloadLegalHandlerReportCsv } from "@/lib/api";
import { LegalDeskBanner, useLegalDesk } from "./legalDesk";

const fmtGBP = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
const WEEK_OPTIONS = [4, 6, 8, 12];

function WeekCell({ w, testid }) {
  const empty = !w.due && !w.done && !w.overdue;
  return (
    <td className={`px-2 py-2 text-center tabular-nums text-xs ${w.current ? "bg-indigo-50/60" : ""}`} data-testid={testid} title={`${w.done} done · ${w.due} due · ${w.overdue} still overdue`}>
      {empty ? <span className="text-slate-300">—</span> : (
        <span>
          <span className={w.done >= w.due && w.due > 0 ? "text-emerald-700 font-semibold" : "text-slate-800 font-semibold"}>{w.done}</span>
          <span className="text-slate-400"> / {w.due}</span>
          {w.overdue > 0 && <span className="ml-1 text-[10px] font-bold text-red-700" data-testid={`${testid}-overdue`}>{w.overdue} late</span>}
        </span>
      )}
    </td>
  );
}

export default function LegalHandlerReport() {
  const navigate = useNavigate();
  useLegalDesk();
  const [weeksBack, setWeeksBack] = useState(6);
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { setRep(await getLegalHandlerReport({ weeks_back: weeksBack, weeks_fwd: 1 })); }
    catch (e) { toast.error("Report failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  }, [weeksBack]);
  useEffect(() => { load(); }, [load]);
  const csv = async () => {
    setDownloading(true);
    try {
      const blob = await downloadLegalHandlerReportCsv({ weeks_back: weeksBack, weeks_fwd: 1 });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `legal-handler-report-${rep?.today || new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Handler report CSV downloaded");
    } catch (e) { toast.error("CSV failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setDownloading(false); }
  };
  const rows = rep?.rows || [], weeks = rep?.weeks || [], t = rep?.totals;
  return (
    <div className="p-4 space-y-3 max-w-[1600px] mx-auto" data-testid="legal-handler-report">
      <LegalDeskBanner />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => navigate("/legal/case-tracker")} data-testid="handler-report-back"><ArrowLeft className="w-4 h-4" /></Button>
          <Users className="w-5 h-5 text-red-700 shrink-0" />
          <div className="min-w-0">
            <h1 className="text-base font-semibold whitespace-nowrap">Legal handler report</h1>
            <p className="text-[11px] text-muted-foreground truncate">Each cell: actions <strong>done / due</strong> in the week, with anything still overdue in red. Owner of an action = the diary owner, else the file's legal owner, else its account handler.{rep ? ` Period ${new Date(rep.period.from + "T00:00:00").toLocaleDateString("en-GB")} – ${new Date(rep.period.to + "T00:00:00").toLocaleDateString("en-GB")}.` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="inline-flex items-center rounded-md border bg-white text-[11px]" data-testid="handler-report-weeks">
            {WEEK_OPTIONS.map((n) => (
              <button key={n} type="button" onClick={() => setWeeksBack(n)} data-testid={`handler-report-weeks-${n}`}
                className={`px-2.5 h-8 first:rounded-l-md last:rounded-r-md ${weeksBack === n ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"}`}>{n} wks</button>
            ))}
          </div>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={load} disabled={loading} data-testid="handler-report-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />} Refresh
          </Button>
          <Button size="sm" className="h-8 text-xs bg-red-700 hover:bg-red-800 text-white" onClick={csv} disabled={downloading || !rep} data-testid="handler-report-download-csv">
            {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />} Download CSV
          </Button>
        </div>
      </div>

      {loading && !rep ? <div className="py-16 flex justify-center" data-testid="handler-report-loading"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> : rep && (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs" data-testid="handler-report-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 bg-slate-50">Handler</th>
                  <th className="text-right px-2 py-2">Open files</th><th className="text-right px-2 py-2">Balance</th>
                  <th className="text-right px-2 py-2">Open actions</th><th className="text-right px-2 py-2">Overdue now</th><th className="text-right px-2 py-2">Closed in period</th>
                  {weeks.map((w) => <th key={w.start} className={`px-2 py-2 text-center whitespace-nowrap ${["This week", "Next week"].includes(w.label) ? "text-indigo-900" : ""}`}>{w.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={6 + weeks.length} className="py-8 text-center text-muted-foreground" data-testid="handler-report-empty">No legal files or diary actions yet.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.handler} className="border-t hover:bg-slate-50" data-testid={`handler-report-row-${r.handler.replace(/\s+/g, "-").toLowerCase()}`}>
                    <td className={`px-3 py-2 font-medium sticky left-0 bg-white ${r.handler === "Unassigned" ? "italic text-slate-500" : "text-slate-900"}`}>{r.handler === "Unassigned" ? "Unassigned" : `⚖ ${r.handler}`}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.open_files}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-600">{fmtGBP(r.balance)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.open_actions}</td>
                    <td className={`px-2 py-2 text-right tabular-nums font-semibold ${r.overdue_now ? "text-red-700" : "text-emerald-700"}`}>{r.overdue_now}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.closed_in_period}</td>
                    {r.weeks.map((w) => <WeekCell key={w.start} w={w} testid={`handler-report-cell-${r.handler.replace(/\s+/g, "-").toLowerCase()}-${w.start}`} />)}
                  </tr>
                ))}
              </tbody>
              {t && (
                <tfoot className="bg-slate-50 border-t-2 border-slate-300 font-semibold">
                  <tr data-testid="handler-report-totals">
                    <td className="px-3 py-2 sticky left-0 bg-slate-50">Total</td>
                    <td className="px-2 py-2 text-right tabular-nums">{t.open_files}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtGBP(t.balance)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{t.open_actions}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${t.overdue_now ? "text-red-700" : "text-emerald-700"}`}>{t.overdue_now}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{t.closed_in_period}</td>
                    {t.weeks.map((w) => <WeekCell key={w.start} w={w} testid={`handler-report-total-${w.start}`} />)}
                  </tr>
                </tfoot>
              )}
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
